import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface RecipeCardMapping {
  recipeId: string;
  quantity: number;
}

export interface InventoryMapping {
  itemCode: string;
  quantity: number;
  parcelOnly: boolean;
}

export interface MenuItem {
  itemId: string;
  name: string;
  price: number;
  salePrice?: number;
  parcelCharge?: number;
  category: string;
  description?: string;
  imageUrl?: string;
  isAvailable: boolean;
  recipeCardMappings?: RecipeCardMapping[];
  inventoryMappings?: InventoryMapping[];
  perServeQuantity?: {
    quantity: number;
    unit: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface accountItem {
  id:string,
  accountId:string,
  accountName: string;
  cashBalance: number;
  accountBalance: number;
  description: string;
  status: string;
  accountType: string;
  bankName?: string;
  accountNumber?: string;
  accountTypeDetail?: "savings" | "current";
  ifscCode?: string;
  branch?: string;
  /** OD only: outstanding drawn to borrower accounts (bank/counter), keyed by accountId */
  odUtilizationByBorrower?: Record<string, number>;
  createdBy: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Order {
  orderId: string;
  customer_name?: string;
  customer_phone?: string;
  items: any[];
  total: number;
  status: string;
  payment_method?: string;
  createdAt: string;
}

export interface OrderStats {
  totalOrders: number;
  revenue: number;
  pendingOrders: number;
}

export interface Broadcast {
  broadcastId: string;
  message?: string;
  phoneNumbers?: string[];
  status: string;
  createdAt: string;
  completedAt?: string;
  successCount?: number;
  failedCount?: number;
  totalCount?: number;
  recipientCount?: number;
  failedPhoneNumbers?: string[];
}

export interface Template {
  name: string;
  status: string;
  category?: string;
}

export interface InventoryItem {
  itemCode: string;
  name: string;
  description?: string;
  unit: string;
  onHand: number;
  perUnitPrice?: number;
  avgDailyUsage?: number;
  leadTimeDays?: number;
  safetyStockDays?: number;
  vendors?: string[]; // Array of vendor IDs
  createdAt?: string;
  updatedAt?: string;
}

export interface FoodProduction {
  productionId: string; // Unique ID for each production instance
  recipeId: string;
  recipeName: string;
  plannedBaseQuantity: number;
  actualBaseQuantity: number | null;
  remainingQuantity: number; // Added to track available stock
  status: 'planned' | 'produced' | 'over'; // Added 'over' status
  plannedBy: string;
  plannedAt: string;
  producedBy?: string | null;
  producedAt?: string | null;
  counterAccountId: string;
  lastOrderId?: string;
  lastDeductedBy?: string;
}

export interface CarryForwardRecipe {
  recipeId: string;
  availableQuantity: number;
  unit: string;
  sourceDate: string;
  source: 'carryforward';
}

export interface CounterSession {
  sessionId: string;
  sessionDate: string; // Date when session was opened
  status: 'opened' | 'closed'; // Counter status
  openedAt: string;
  closedAt: string | null;
  openingBalance: {
    "cashBalance":number,
    "accountBalance":number
  } | null;
  closingBalance: {
    "cashBalance":number,
    "accountBalance":number
  } | null;
  openedBy: string;
  closedBy: string | null;
  leftoverItems?: Array<{
    productionId: string; // Changed from menuItemId to productionId
    leftoverQuantity: number;
    carryForward: boolean;
    wastageQuantity?: number;
    wastageCost?: number;
  }>;
  expectedBalance: {
    "cashBalance":number,
    "accountBalance":number
  } | null;
  amountVariance: {
    "cash":number,
    "account":number
  } | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface DailyOperations {
  operationDate: string;
  foodProduction: FoodProduction[];
  // carryForwardRecipes?: CarryForwardRecipe[];
  // Multiple sessions support
  counterSessions?: CounterSession[];
  currentSessionId?: string | null;
  // Legacy fields for backward compatibility
  // openingBalance: {
  //   "cashBalance":number,
  //   "accountBalance":number
  // } | null;
  // closingBalance: {
  //   "cashBalance":number,
  //   "accountBalance":number
  // } | null;
  // openedBy: string | null;
  updatedAt: string | null;
  // closedBy: string | null;
  createdAt: string | null;
  // leftoverItems: Array<{
  //   menuItemId: string;
  //   leftoverQuantity: number;
  //   carryForward: boolean;
  //   wastageQuantity?: number;
  //   wastageCost?: number;
  // }>;
  // expectedCash: number;
  // actualCash: number | null;
  // cashVariance: number | null;
  activeSession: CounterSession;
  isEditable: boolean;
}

export interface CounterStatus {
  counterStatus: 'closed' | 'opened';
  openingBalance: number | null;
  closingBalance: number | null;
  expectedCash: number;
  actualCash: number | null;
  cashVariance: number | null;
  openedAt: string | null;
  closedAt: string | null;
}

export interface RecipeIngredient {
  itemCode: string;
  itemName: string;
  unit: string;
  quantityPerBase: number;
  isBaseIngredient: boolean;
}

export interface RecipeCard {
  recipeId: string;
  recipeName: string;
  menuItemId?: string;
  category?: string;
  description?: string;
  baseQuantity: {
    itemCode: string;
    itemName: string;
    unit: string;
    baseAmount: number;
  };
  ingredients: RecipeIngredient[];
  yieldProduced?: {
    quantity: number;
    unit: string;
  };
  costPerBase?: number;
  // Carry-forward tracking
  carryForwardQuantity?: number;
  carryForwardUnit?: string;
  lastCarryForwardUpdate?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface RecipeCardMapping {
  recipeId: string;
  quantity: number;
}

export interface MenuItem {
  itemId: string;
  name: string;
  price: number;
  salePrice?: number;
  parcelCharge?: number;
  category: string;
  description?: string;
  imageUrl?: string;
  isAvailable: boolean;
  recipeCardMappings?: RecipeCardMapping[];
  inventoryMappings?: InventoryMapping[];
  perServeQuantity?: {
    quantity: number;
    unit: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

@Injectable({
  providedIn: 'root',
})
export class Api {
  private http = inject(HttpClient);
  private baseUrl = environment.apiBaseUrl;

  // Auth endpoints
  requestOTP(phone: string): Observable<{ message: string; expiresIn: number }> {
    return this.http.post<{ message: string; expiresIn: number }>(
      `${this.baseUrl}/api/auth/login/request-otp`,
      { phone }
    );
  }

  verifyOTP(phone: string, otp: string): Observable<{ token: string; user: any }> {
    return this.http.post<{ token: string; user: any }>(
      `${this.baseUrl}/api/auth/login/verify-otp`,
      { phone, otp }
    );
  }

  getAccountList(type?: string, limit?: number): Observable<{ accounts: accountItem[] }> {
    let params = new HttpParams();
    if (limit) params = params.set('limit', limit.toString());
    if (type) params = params.set('type', type.toString());
    return this.http.get<{ accounts: accountItem[] }>(`${this.baseUrl}/api/admin/accounts`, { params });
  }


  getAccount(accountId: string): Observable<{ accountData: accountItem }> {
    return this.http.get<{ accountData: accountItem }>(`${this.baseUrl}/api/admin/accounts/${accountId}`);
  }

  createAccount(accountData: Partial<accountItem>): Observable<{ message: string; accountData: accountItem }> {
    return this.http.post<{ message: string; accountData: accountItem }>(
      `${this.baseUrl}/api/admin/accounts`,
      accountData
    );
  }

  updateAccount(accountId: string, updateData: Partial<accountItem>): Observable<{ message: string; accountData: accountItem }> {
    return this.http.put<{ message: string; accountData: accountItem }>(
      `${this.baseUrl}/api/admin/accounts/${accountId}`,
      updateData
    );
  }

    getMenuItems(category?: string, limit?: number): Observable<{ menuItems: MenuItem[] }> {
    let params = new HttpParams();
    if (category) params = params.set('category', category);
    if (limit) params = params.set('limit', limit.toString());
    return this.http.get<{ menuItems: MenuItem[] }>(`${this.baseUrl}/api/admin/menu`, { params });
  }

  getMenuItem(itemId: string): Observable<{ menuItem: MenuItem }> {
    return this.http.get<{ menuItem: MenuItem }>(`${this.baseUrl}/api/admin/menu/${itemId}`);
  }

  createMenuItem(item: Partial<MenuItem>): Observable<{ message: string; menuItem: MenuItem }> {
    return this.http.post<{ message: string; menuItem: MenuItem }>(
      `${this.baseUrl}/api/admin/menu`,
      item
    );
  }

  updateMenuItem(itemId: string, item: Partial<MenuItem>): Observable<{ message: string; menuItem: MenuItem }> {
    return this.http.put<{ message: string; menuItem: MenuItem }>(
      `${this.baseUrl}/api/admin/menu/${itemId}`,
      item
    );
  }

  deleteMenuItem(itemId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.baseUrl}/api/admin/menu/${itemId}`);
  }

  toggleMenuItemAvailability(itemId: string, isAvailable: boolean): Observable<{ message: string; menuItem: MenuItem }> {
    return this.http.put<{ message: string; menuItem: MenuItem }>(
      `${this.baseUrl}/api/admin/menu/${itemId}/availability`,
      { isAvailable }
    );
  }

  // Order endpoints
  getOrders(filters?: { status?: string; startDate?: string; endDate?: string; limit?: number }): Observable<{ orders: Order[]; total: number }> {
    let params = new HttpParams();
    if (filters?.status) params = params.set('status', filters.status);
    if (filters?.startDate) params = params.set('startDate', filters.startDate);
    if (filters?.endDate) params = params.set('endDate', filters.endDate);
    if (filters?.limit) params = params.set('limit', filters.limit.toString());
    return this.http.get<{ orders: Order[]; total: number }>(`${this.baseUrl}/api/admin/orders`, { params });
  }

  getOrderStats(startDate?: string, endDate?: string): Observable<{ stats: OrderStats }> {
    let params = new HttpParams();
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    return this.http.get<{ stats: OrderStats }>(`${this.baseUrl}/api/admin/orders/stats`, { params });
  }

  // Broadcast endpoints
  sendBroadcast(message: string, phoneNumbers?: string[]): Observable<{ message: string; status: string; broadcastId: string }> {
    const payload: any = { message };
    if (phoneNumbers && phoneNumbers.length > 0) {
      payload.phoneNumbers = phoneNumbers;
    }
    return this.http.post<{ message: string; status: string; broadcastId: string }>(
      `${this.baseUrl}/api/admin/broadcast`,
      payload
    );
  }

  getBroadcasts(): Observable<{ broadcasts: Broadcast[]; count: number }> {
    return this.http.get<{ broadcasts: Broadcast[]; count: number }>(`${this.baseUrl}/api/admin/broadcast`);
  }

  getBroadcast(broadcastId: string, createdAt?: string): Observable<{ broadcast: Broadcast }> {
    let params = new HttpParams();
    if (createdAt) params = params.set('createdAt', createdAt);
    return this.http.get<{ broadcast: Broadcast }>(`${this.baseUrl}/api/admin/broadcast/${broadcastId}`, { params });
  }

  getTemplates(): Observable<{ templates: Template[] }> {
    return this.http.get<{ templates: Template[] }>(`${this.baseUrl}/api/admin/broadcast/templates`);
  }

  sendTemplateMessages(phoneNumbers: string[], templateName: string, languageCode: string = 'en'): Observable<{ message: string; results: any }> {
    return this.http.post<{ message: string; results: any }>(
      `${this.baseUrl}/api/admin/broadcast/template`,
      { phoneNumbers, templateName, languageCode }
    );
  }

  // Inventory endpoints
  getInventoryItems(): Observable<{ items: InventoryItem[] }> {
    return this.http.get<{ items: InventoryItem[] }>(`${this.baseUrl}/api/admin/inventory`);
  }

  getInventoryItem(itemCode: string): Observable<{ item: InventoryItem }> {
    return this.http.get<{ item: InventoryItem }>(`${this.baseUrl}/api/admin/inventory/${itemCode}`);
  }

  createInventoryItem(item: Partial<InventoryItem>): Observable<{ item: InventoryItem }> {
    return this.http.post<{ item: InventoryItem }>(`${this.baseUrl}/api/admin/inventory`, item);
  }

  updateInventoryItem(itemCode: string, item: Partial<InventoryItem>): Observable<{ item: InventoryItem; message: string }> {
    return this.http.put<{ item: InventoryItem; message: string }>(`${this.baseUrl}/api/admin/inventory/${itemCode}`, item);
  }

  deleteInventoryItem(itemCode: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.baseUrl}/api/admin/inventory/${itemCode}`);
  }

  // Ledger endpoints
  getLedgerEntries(itemCode?: string, startDate?: string, endDate?: string, limit?: number): Observable<{ entries: LedgerEntry[] }> {
    let params = new HttpParams();
    if (itemCode) params = params.set('itemCode', itemCode);
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    if (limit) params = params.set('limit', limit.toString());
    return this.http.get<{ entries: LedgerEntry[] }>(`${this.baseUrl}/api/admin/ledger`, { params });
  }

  // Financial Ledger endpoints
  getFinancialLedgerEntries(filters: {
    accountId?: string;
    counterId?: string;
    accountType?: string;
    transactionType?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
  }): Observable<{ entries: FinancialLedgerEntry[] }> {
    let params = new HttpParams();
    if (filters.accountId) params = params.set('accountId', filters.accountId);
    if (filters.counterId) params = params.set('counterId', filters.counterId);
    if (filters.accountType) params = params.set('accountType', filters.accountType);
    if (filters.transactionType) params = params.set('transactionType', filters.transactionType);
    if (filters.startDate) params = params.set('startDate', filters.startDate);
    if (filters.endDate) params = params.set('endDate', filters.endDate);
    if (filters.limit) params = params.set('limit', filters.limit.toString());
    return this.http.get<{ entries: FinancialLedgerEntry[] }>(`${this.baseUrl}/api/admin/financial-ledger`, { params });
  }

  getFinancialLedgerSummary(filters: {
    accountId?: string;
    counterId?: string;
    accountType?: string;
    transactionType?: string;
    startDate?: string;
    endDate?: string;
  }): Observable<{ summary: FinancialLedgerSummary }> {
    let params = new HttpParams();
    if (filters.accountId) params = params.set('accountId', filters.accountId);
    if (filters.counterId) params = params.set('counterId', filters.counterId);
    if (filters.accountType) params = params.set('accountType', filters.accountType);
    if (filters.transactionType) params = params.set('transactionType', filters.transactionType);
    if (filters.startDate) params = params.set('startDate', filters.startDate);
    if (filters.endDate) params = params.set('endDate', filters.endDate);
    return this.http.get<{ summary: FinancialLedgerSummary }>(`${this.baseUrl}/api/admin/financial-ledger/summary`, { params });
  }

  transferFunds(data: {
    fromAccountType: 'counter' | 'bank' | 'od';
    fromAccountId: string;
    toAccountType: 'counter' | 'bank' | 'od';
    toAccountId: string;
    amount: number;
    balanceType?: 'cash' | 'account';
    description?: string;
  }): Observable<{ transfer: any }> {
    return this.http.post<{ transfer: any }>(`${this.baseUrl}/api/admin/transfers`, data);
  }

  recordExpense(data: {
    accountType: 'counter' | 'bank';
    accountId: string;
    amount: number;
    category: string;
    balanceType?: 'cash' | 'account';
    description?: string;
  }): Observable<{ expense: any }> {
    return this.http.post<{ expense: any }>(`${this.baseUrl}/api/admin/expenses`, data);
  }

  // Purchase Order endpoints
  getPurchaseOrders(filters?: { vendorId?: string; status?: string; limit?: number }): Observable<{ purchaseOrders: PurchaseOrder[] }> {
    let params = new HttpParams();
    if (filters?.vendorId) params = params.set('vendorId', filters.vendorId);
    if (filters?.status) params = params.set('status', filters.status);
    if (filters?.limit) params = params.set('limit', filters.limit.toString());
    return this.http.get<{ purchaseOrders: PurchaseOrder[] }>(`${this.baseUrl}/api/admin/purchase-orders`, { params });
  }

  getPurchaseOrder(poId: string): Observable<{ purchaseOrder: PurchaseOrder }> {
    return this.http.get<{ purchaseOrder: PurchaseOrder }>(`${this.baseUrl}/api/admin/purchase-orders/${poId}`);
  }

  createPurchaseOrder(vendorId: string, items: Array<{ itemCode: string; quantity: number; notes?: string }>): Observable<{ purchaseOrder: PurchaseOrder }> {
    return this.http.post<{ purchaseOrder: PurchaseOrder }>(
      `${this.baseUrl}/api/admin/purchase-orders`,
      { vendorId, items }
    );
  }

  updatePurchaseOrder(poId: string, updateData: Partial<PurchaseOrder>): Observable<{ purchaseOrder: PurchaseOrder }> {
    return this.http.put<{ purchaseOrder: PurchaseOrder }>(
      `${this.baseUrl}/api/admin/purchase-orders/${poId}`,
      updateData
    );
  }

  sendPurchaseOrder(poId: string): Observable<{ purchaseOrder: PurchaseOrder }> {
    return this.http.post<{ purchaseOrder: PurchaseOrder }>(
      `${this.baseUrl}/api/admin/purchase-orders/${poId}/send`,
      {}
    );
  }

  // confirmPurchaseOrder(poId: string): Observable<{ purchaseOrder: PurchaseOrder }> {
  //   return this.http.post<{ purchaseOrder: PurchaseOrder }>(
  //     `${this.baseUrl}/api/admin/purchase-orders/${poId}/confirm`,
  //     {}
  //   );
  // }

  deletePurchaseOrder(poId: string): Observable<{ purchaseOrder: PurchaseOrder }> {
    return this.http.delete<{ purchaseOrder: PurchaseOrder }>(
      `${this.baseUrl}/api/admin/purchase-orders/${poId}`
    );
  }

  resendPOMessage(poId: string): Observable<{ purchaseOrder: PurchaseOrder }> {
    return this.http.post<{ purchaseOrder: PurchaseOrder }>(
      `${this.baseUrl}/api/admin/purchase-orders/${poId}/resend`,
      {}
    );
  }

  // Vendor endpoints
  getVendors(filters?: { category?: string; status?: string; limit?: number }): Observable<{ vendors: Vendor[] }> {
    let params = new HttpParams();
    if (filters?.category) params = params.set('category', filters.category);
    if (filters?.status) params = params.set('status', filters.status);
    if (filters?.limit) params = params.set('limit', filters.limit.toString());
    return this.http.get<{ vendors: Vendor[] }>(`${this.baseUrl}/api/admin/vendors`, { params });
  }

  getVendor(vendorId: string): Observable<{ vendor: Vendor; items?: InventoryItem[] }> {
    return this.http.get<{ vendor: Vendor; items?: InventoryItem[] }>(`${this.baseUrl}/api/admin/vendors/${vendorId}`);
  }

  createVendor(vendorData: Partial<Vendor>): Observable<{ vendor: Vendor }> {
    return this.http.post<{ vendor: Vendor }>(`${this.baseUrl}/api/admin/vendors`, vendorData);
  }

  updateVendor(vendorId: string, vendorData: Partial<Vendor>): Observable<{ vendor: Vendor }> {
    return this.http.put<{ vendor: Vendor }>(`${this.baseUrl}/api/admin/vendors/${vendorId}`, vendorData);
  }

  addItemToVendor(vendorId: string, itemData: Partial<VendorItem>): Observable<{ item: VendorItem }> {
    return this.http.post<{ item: VendorItem }>(
      `${this.baseUrl}/api/admin/vendors/${vendorId}/items`,
      itemData
    );
  }

  removeItemFromVendor(vendorId: string, itemCode: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(
      `${this.baseUrl}/api/admin/vendors/${vendorId}/items/${itemCode}`
    );
  }

  updateVendorItemPrice(vendorId: string, itemCode: string, newPrice: number): Observable<{ item: VendorItem }> {
    return this.http.put<{ item: VendorItem }>(
      `${this.baseUrl}/api/admin/vendors/${vendorId}/items/${itemCode}/price`,
      { newPrice }
    );
  }

  getVendorsForItem(itemCode: string): Observable<{ vendors: Vendor[] }> {
    return this.http.get<{ vendors: Vendor[] }>(`${this.baseUrl}/api/admin/vendors/items/${itemCode}`);
  }

  // Receipt endpoints
  createReceipt(purchaseOrderId: string): Observable<{ receipt: Receipt }> {
    return this.http.post<{ receipt: Receipt }>(
      `${this.baseUrl}/api/admin/receipts`,
      {
        action: 'create',
        purchaseOrderId: purchaseOrderId
      }
    );
  }

  getReceipt(receiptId: string): Observable<{ receipt: Receipt }> {
    return this.http.get<{ receipt: Receipt }>(`${this.baseUrl}/api/admin/receipts/${receiptId}`);
  }

  addItemToReceipt(receiptId: string, itemData: ReceiptItemData): Observable<{ item: ReceiptItem }> {
    return this.http.post<{ item: ReceiptItem }>(
      `${this.baseUrl}/api/admin/receipts`,
      {
        action: 'addItem',
        receiptId: receiptId,
        itemData: itemData
      }
    );
  }

  addItemsToReceipt(receiptId: string, itemsData: ReceiptItemData[]): Observable<{ items: ReceiptItem[] }> {
    return this.http.post<{ items: ReceiptItem[] }>(
      `${this.baseUrl}/api/admin/receipts`,
      {
        action: 'addItems',
        receiptId: receiptId,
        itemsData: itemsData
      }
    );
  }


  submitReceiptForBilling(purchaseOrderId: string, billingData: {
    items: ReceiptItemData[];
    invoiceNumber: string;
    invoiceDate: string;
    invoiceAmount: number;
    vendorPaymentAmount: number;
  }): Observable<{ receipt: Receipt; bill: any }> {
    return this.http.post<{ receipt: Receipt; bill: any }>(
      `${this.baseUrl}/api/admin/purchase-orders/${purchaseOrderId}/submit-billing`,
      billingData
    );
  }

  getReceiptsForPO(purchaseOrderId: string): Observable<{ receipts: Receipt[] }> {
    return this.http.get<{ receipts: Receipt[] }>(`${this.baseUrl}/api/admin/receipts/po/${purchaseOrderId}`);
  }

  // Payment endpoints
  getPayments(filters?: { vendorId?: string; paymentStatus?: string; purchaseOrderId?: string; limit?: number }): Observable<{ payments: Payment[] }> {
    let params = new HttpParams();
    if (filters?.vendorId) params = params.set('vendorId', filters.vendorId);
    if (filters?.paymentStatus) params = params.set('status', filters.paymentStatus);
    if (filters?.purchaseOrderId) params = params.set('purchaseOrderId', filters.purchaseOrderId);
    if (filters?.limit) params = params.set('limit', filters.limit.toString());
    return this.http.get<{ payments: Payment[] }>(`${this.baseUrl}/api/admin/payments`, { params });
  }

  getPayment(paymentId: string): Observable<{ payment: Payment }> {
    return this.http.get<{ payment: Payment }>(`${this.baseUrl}/api/admin/payments/${paymentId}`);
  }

  recordPayment(purchaseOrderId: string, paymentData: PaymentData): Observable<{ payment: Payment }> {
    return this.http.post<{ payment: Payment }>(
      `${this.baseUrl}/api/admin/payments`,
      { purchaseOrderId, ...paymentData }
    );
  }

  updatePayment(paymentId: string, paymentData: Partial<PaymentData>): Observable<{ payment: Payment }> {
    return this.http.put<{ payment: Payment }>(
      `${this.baseUrl}/api/admin/payments/${paymentId}`,
      paymentData
    );
  }

  getOverduePayments(vendorId?: string): Observable<{ payments: Payment[] }> {
    let params = new HttpParams();
    if (vendorId) params = params.set('vendorId', vendorId);
    return this.http.get<{ payments: Payment[] }>(`${this.baseUrl}/api/admin/payments/overdue`, { params });
  }

  // Daily Operations / Food Production endpoints
  getCounterDailyOperations(date: string, counterAccountId: string): Observable<{ operations: DailyOperations }> {
    let params = new HttpParams();
    params = params.set('counterAccountId', counterAccountId);
    params = params.set('date', date);
    return this.http.get<{ operations: DailyOperations }>(`${this.baseUrl}/api/admin/daily-operations`, {
      params});
  }

  getDailyOperations(date: string, counterAccountId?: string): Observable<{ operations: DailyOperations }> {
    let params = new HttpParams();
    params = params.set('date', date);
    if (counterAccountId) {
      params = params.set('counterAccountId', counterAccountId);
    }
    return this.http.get<{ operations: DailyOperations }>(`${this.baseUrl}/api/admin/daily-operations`, {
      params});
  }

  // Add a new food production
  addFoodProduction(data: { operationDate: string; recipeId: string; plannedBaseQuantity: number, counterAccountId: string }): Observable<{ operations: DailyOperations }> {
    return this.http.post<{ operations: DailyOperations }>(`${this.baseUrl}/api/admin/daily-operations/food-production`, data);
  }

  // Update a food production
  updateFoodProduction(productionId: string, data: { operationDate: string; plannedBaseQuantity?: number, counterAccountId: string  }): Observable<{ operations: DailyOperations }> {
    return this.http.put<{ operations: DailyOperations }>(`${this.baseUrl}/api/admin/daily-operations/food-production/${productionId}`, data);
  }

  // Delete a food production
  deleteFoodProduction(productionId: string, operationDate: string): Observable<{ operations: DailyOperations }> {
    const params = new HttpParams().set('operationDate', operationDate);
    return this.http.delete<{ operations: DailyOperations }>(`${this.baseUrl}/api/admin/daily-operations/food-production/${productionId}`, { params });
  }

  // Mark production as produced
  markProductionAsProduced(productionId: string, data: { operationDate: string }): Observable<{ operations: DailyOperations }> {
    return this.http.put<{ operations: DailyOperations }>(`${this.baseUrl}/api/admin/daily-operations/food-production/${productionId}/produce`, data);
  }


  updateActualQuantity(data: { operationDate: string; recipeId: string; actualBaseQuantity: number }): Observable<{ operations: DailyOperations }> {
    return this.http.put<{ operations: DailyOperations }>(`${this.baseUrl}/api/admin/daily-operations/actual-quantity`, data);
  }

  // Counter Operations endpoints (session-based)
  openCounter(data: { sessionDate: string; counterAccountId:string; operation:'OpenCounter' }): Observable<{ session: CounterSession; success: boolean }> {
    return this.http.post<{ session: CounterSession; success: boolean }>(`${this.baseUrl}/api/admin/counter/management`, data);
  }

  placeKOT(data: { items: Array<{ menuItemId: string; quantity: number }>; paymentMethod: string; deliveryType: 'dine_in' | 'parcel'; sessionId?: string ;counterAccountId:string}): Observable<{ orderId: string; kotNumber: string; totalAmount: number; deliveryType: string; summary: any }> {
    return this.http.post<{ orderId: string; kotNumber: string; totalAmount: number; deliveryType: string; summary: any }>(`${this.baseUrl}/api/admin/counter/kot`, data);
  }

  closeCounter(data: { sessionId: string; closingBalance: {cashBalance:number, accountBalance:number}; leftoverItems: Array<{ productionId: string; leftoverQuantity: number; carryForward: boolean }> }): Observable<{ session: CounterSession; success: boolean; totalWastageCost?: number; message?: string }> {
    return this.http.post<{ session: CounterSession; success: boolean; totalWastageCost?: number; message?: string }>(`${this.baseUrl}/api/admin/counter/close`, data);
  }

  getCounterStatus(date?: string, sessionId?: string): Observable<{ session?: CounterSession; sessions?: CounterSession[]; date?: string }> {
    let params = new HttpParams();
    if (date) params = params.set('date', date);
    if (sessionId) params = params.set('sessionId', sessionId);
    return this.http.get<{ session?: CounterSession; sessions?: CounterSession[]; date?: string }>(`${this.baseUrl}/api/admin/counter/status`, { params });
  }

  getCounterSessions(counterAccountId: string, date?:string): Observable<{ sessions?: CounterSession[]; date?: string}> {
    return this.http.post<{ sessions?: CounterSession[]; date?: string; operation:'getCounterSessions'}>(`${this.baseUrl}/api/admin/counter/management`,{operation:'getCounterSessions',counterAccountId:counterAccountId, date: date});
  }

  getActiveCounterSession(counterAccountId: string): Observable<{ session: CounterSession | null; activeDate: string | null }> {
    return this.http.post<{ session: CounterSession | null; activeDate: string | null; counterAccount: string; operation:'getActiveSession'}>(`${this.baseUrl}/api/admin/counter/management`,{operation:'getActiveSession',counterAccountId:counterAccountId});
  }

  getCounterHistory(startDate: string, endDate: string, limit: number = 30): Observable<{ history: Array<{
    operationDate: string;
    counterStatus: 'opened' | 'closed';
    openingBalance: number | null;
    closingBalance: number | null;
    expectedCash: number;
    actualCash: number | null;
    cashVariance: number | null;
    openedBy: string | null;
    openedAt: string | null;
    closedBy: string | null;
    closedAt: string | null;
    kotCount: number | string;
  }> }> {
    let params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate)
      .set('limit', limit.toString());
    return this.http.get<{ history: any[] }>(`${this.baseUrl}/api/admin/counter/history`, { params });
  }

  // Recipe Cards endpoints
  getRecipes(filters?: { category?: string; menuItemId?: string; isActive?: boolean }): Observable<{ recipes: RecipeCard[] }> {
    let params = new HttpParams();
    if (filters?.category) params = params.set('category', filters.category);
    if (filters?.menuItemId) params = params.set('menuItemId', filters.menuItemId);
    if (filters?.isActive !== undefined) params = params.set('isActive', filters.isActive.toString());
    return this.http.get<{ recipes: RecipeCard[] }>(`${this.baseUrl}/api/admin/recipes`, { params });
  }

  getRecipe(recipeId: string): Observable<{ recipe: RecipeCard }> {
    return this.http.get<{ recipe: RecipeCard }>(`${this.baseUrl}/api/admin/recipes/${recipeId}`);
  }

  createRecipe(recipeData: Partial<RecipeCard>): Observable<{ recipe: RecipeCard }> {
    return this.http.post<{ recipe: RecipeCard }>(`${this.baseUrl}/api/admin/recipes`, recipeData);
  }

  updateRecipe(recipeId: string, recipeData: Partial<RecipeCard>): Observable<{ recipe: RecipeCard }> {
    return this.http.put<{ recipe: RecipeCard }>(`${this.baseUrl}/api/admin/recipes/${recipeId}`, recipeData);
  }

  deleteRecipe(recipeId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.baseUrl}/api/admin/recipes/${recipeId}`);
  }

  calculateRecipeCost(recipeId: string): Observable<{ costPerBase: number }> {
    return this.http.post<{ costPerBase: number }>(`${this.baseUrl}/api/admin/recipes/${recipeId}/calculate-cost`, {});
  }
}

// Payment interfaces
export interface Payment {
  paymentId: string;
  purchaseOrderId: string;
  receiptId?: string;
  vendorId: string;
  vendorName: string;
  paymentDate: string;
  dueDate?: string;
  paymentAmount: number;
  paymentMethod?: string;
  paymentStatus: 'pending' | 'partial' | 'paid' | 'overdue';
  paymentReference?: string;
  bankName?: string;
  transactionId?: string;
  transactionDate?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  invoiceAmount: number;
  reconciliationStatus?: string;
  isPartial: boolean;
  partialPayments?: PartialPayment[];
  totalPaid: number;
  remainingAmount: number;
  paidBy: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentData {
  transactionId?: string;
  transactionDate?: string;
  paymentAmount: number;
  paymentMethod?: string;
  paymentReference?: string;
  bankName?: string;
  notes?: string;
  accountId?: string;
  accountType?: 'counter' | 'bank';
  balanceType?: 'cash' | 'account';
}

export interface PartialPayment {
  amount: number;
  transactionId: string;
  transactionDate: string;
  paymentMethod: string;
  paidBy: string;
  paidAt: string;
}

// Purchase Order interfaces
export interface PurchaseOrder {
  purchaseOrderId: string;
  vendorId: string;
  vendorName: string;
  status: 'draft' | 'sent' | 'confirmed' | 'received' | 'partially_received' | 'cancelled';
  items: PurchaseOrderItem[];
  subtotal: number;
  tax: number;
  deliveryCharges: number;
  discount: number;
  grandTotal: number;
  expectedDeliveryDate?: string;
  dueDate?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  notes?: string;
}

export interface PurchaseOrderItem {
  itemCode: string;
  itemName: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  totalPrice: number;
  notes?: string;
}

export interface Vendor {
  vendorId: string;
  name: string;
  contactName?: string;
  phone?: string;
  email?: string;
  address?: string;
  status: 'active' | 'inactive';
  category?: string;
  // DEPRECATED: items is no longer stored in vendor records
  // Items are fetched from inventory items where vendor ID is in vendors array
  items?: VendorItem[];
  defaultPaymentTerms?: {
    creditDays: number;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface VendorItem {
  itemCode: string;
  itemName: string;
  unit: string;
  unitPrice: number;
  isActive: boolean;
  priceTiers?: Array<{
    minQuantity: number;
    unitPrice: number;
  }>;
}

export interface Receipt {
  receiptId: string;
  purchaseOrderId: string;
  vendorId: string;
  vendorName: string;
  receiptDate: string;
  receivedBy: string;
  status: 'pending' | 'completed';
  items: ReceiptItem[];
  totalAmount: number;
  adjustments: {
    discount: number;
    additionalCharges: number;
  };
  finalAmount: number;
  invoiceNumber?: string;
  invoiceDate?: string;
  invoiceAmount: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ReceiptItem {
  itemCode: string;
  itemName: string;
  quantityOrdered: number;
  quantityReceived: number;
  quantityAccepted: number;
  quantityRejected: number;
  rejectionReason?: string;
  unit: string;
  unitPrice: number;
  totalPrice: number;
}

export interface ReceiptItemData {
  itemCode: string;
  itemName?: string; // For custom items not in PO
  quantityOrdered?: number; // For custom items not in PO
  quantityReceived: number;
  quantityAccepted?: number;
  unit?: string; // For custom items not in PO
  unitPrice?: number; // For custom items not in PO
  rejectionReason?: string;
}

export interface InvoiceData {
  invoiceNumber: string;
  invoiceDate: string;
  invoiceAmount: number;
}

export interface LedgerEntry {
  itemCode: string;
  timestamp: string;
  id: string;
  type: string;
  transactionType?: 'credit' | 'debit' | 'neutral';
  delta: number | null;
  quantity?: number | null;
  unit?: string | null;
  before: any;
  after: any;
  actorPhone: string | null;
  recipeDetails?: {
    recipeId: string;
    recipeName: string;
    quantity: number;
    unit: string;
    sourceDate?: string;
  };
  productionDetails?: {
    recipeId: string;
    baseQuantity: number;
  };
  reconciliationId?: string;
  purchaseOrderId?: string;
  receiptId?: string;
  notes?: string;
}

export interface FinancialLedgerEntry {
  transactionId: string;
  transactionDate: string;
  transactionType: 'order_payment' | 'expense' | 'transfer' | 'vendor_payment' | 'manual_adjustment';
  accountType: 'counter' | 'bank';
  accountId: string;
  counterId?: string;
  amount: number;
  balanceType?: 'cash' | 'account';
  direction: 'credit' | 'debit';
  description?: string;
  referenceId?: string;
  referenceType?: string;
  category?: string;
  metadata?: any;
  createdBy: string;
  createdAt: string;
}

export interface FinancialLedgerSummary {
  totalCredits: number;
  totalDebits: number;
  netBalance: number;
  byType: Record<string, number>;
}
